import { personName } from '@onwardcare/core';

/** The account flag that turns on bookings by Onward support for other accounts (ON-4717). */
export const MANAGE_ALL_ACCOUNTS_PERMISSION_HANDLING_FLAG =
  'manage_all_accounts_permission_handling';

/** How a coordinator booking as Onward support is named instead of by their own name. */
export const ONWARD_SUPPORT_AGENT_NAME = 'Onward Support Agent';

type Coordinator = {
  accountId: string;
  firstName?: string | null;
  lastName?: string | null;
};

/**
 * Whether a ride's coordinator booked it as Onward support: Onward staff book rides for other
 * accounts as themselves (ON-4717). That is a coordinator from outside the ride's account, in an
 * account with the manage_all_accounts_permission_handling flag on.
 *
 * The Dashboard only shows rides of the signed-in account, so the ride's account is the session's,
 * and its flags are the session account's own (Account.featureFlags, never anyone's personal ones):
 * the same flags the backend rule (Ride#booked_by_onward_support?) reads.
 */
export const bookedByOnwardSupport = (
  coordinator: Pick<Coordinator, 'accountId'> | null | undefined,
  accountId: string | null | undefined,
  featureFlags: ReadonlyArray<string> | null | undefined,
): boolean =>
  coordinator?.accountId != null &&
  accountId != null &&
  String(coordinator.accountId) !== String(accountId) &&
  (featureFlags ?? []).includes(MANAGE_ALL_ACCOUNTS_PERMISSION_HANDLING_FLAG);

/** The name a ride's coordinator is shown by: Onward Support Agent, or their own. */
export const coordinatorName = (
  coordinator: Coordinator | null | undefined,
  accountId: string | null | undefined,
  featureFlags: ReadonlyArray<string> | null | undefined,
): string =>
  bookedByOnwardSupport(coordinator, accountId, featureFlags)
    ? ONWARD_SUPPORT_AGENT_NAME
    : personName(coordinator);
